import { Button, Grid2, Skeleton, Stack, Typography } from '@mui/material'
import { useCallback, useState } from 'react'
import { IconButton, Icons, SkeletonText } from 'utoniq-ui'

import {
  Maybe,
  StoreCollectionDetailsQuery,
  StoreCollectionItemsConnectionFragment,
  StoreItemFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Header, HeaderImageArea } from '@/components/layouts'
import { StoreItemArea, StoreItemAreaSkeleton } from '@/components/organisms'
import { ScrollFetcher, Tabs } from '@/components/organisms/functionals'
import { ArtistThemeProvider, styles } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { individualDisplayResponsive } from '@/core/individual'
import { App } from '@/core/native'
import { useAnalytics } from '@/lib/analytics'

export * from './fragments'

export type State = {
  loading: boolean
  hasNextPage: boolean
  collection: Maybe<StoreCollectionDetailsQuery['collection']>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onChangeLabel: (isFirstLabel: boolean, name: string) => Promise<void>
  onFetchMore: (isFirstLabel: boolean, label?: string) => Promise<void>
  onClickShare: () => Promise<void>
}

export const StoreCollectionDetailsTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onClickShare,
  onChangeLabel,
}) => {
  const [currentTab, setCurrentTab] = useState<number>(0)
  const thumbnailUri =
    state.collection?.thumbnail.mediumUri ??
    state.collection?.thumbnail.uri ??
    ''

  const handleChangeTab = useCallback(
    async (number: number) => {
      if (!state.collection?.labels?.length) {
        return
      }
      setCurrentTab(number)
      await onChangeLabel(
        number === 0,
        state.collection.labels[number]?.name ?? ''
      )
    },
    [onChangeLabel, state.collection?.labels]
  )

  const displayResponsive = individualDisplayResponsive()

  return (
    <ArtistThemeProvider>
      <BaseLayout maxWidth={displayResponsive ? 1120 : undefined}>
        <CollectionImageCell
          loading={state.loading}
          imageUrl={thumbnailUri}
          responsive={displayResponsive}
        />
        <CollectionDetailCell
          loading={state.loading}
          collection={state.collection}
          onClickShare={onClickShare}
          responsive={displayResponsive}
        />
        <Tabs
          labels={
            state.collection?.labels?.map(label => label?.name ?? '') ?? []
          }
          hiddenLabel={!state.collection?.labels?.length}
          value={currentTab}
          disableAnimateTransitions
          onChange={handleChangeTab}
        >
          {(state.collection?.labels || [null]).map((label, index) => (
            <StoreCollectionListCell
              key={index}
              loading={state.loading}
              hasNextPage={state.hasNextPage}
              collectionItems={
                state.collection?.collectionItems as
                  | StoreCollectionItemsConnectionFragment
                  | undefined
              }
              onFetchMore={() => onFetchMore(index === 0, label?.name)}
              artistId={state.collection?.artistId ?? ''}
              responsive={displayResponsive}
            />
          ))}
        </Tabs>
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

type CollectionImageCellProps = {
  loading: State['loading']
  imageUrl?: string | null
  responsive: boolean
}
export const CollectionImageCell: Cell<CollectionImageCellProps> = ({
  loading,
  imageUrl,
  responsive,
}) => {
  const { router, generatePath, routerPages } =
    useRouter<{ artistId: string }>()

  if (loading && !imageUrl) {
    return (
      <CellFrame>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={212}
          sx={{
            pt: styles.safeAreaTop,
            '& > *': { visibility: 'visible' },
          }}
        >
          <Header
            color="transparent"
            elevation={0}
            sx={{ backgroundColor: 'transparent' }}
            left={
              <Stack
                sx={{
                  borderRadius: '50%',
                  width: 44,
                  height: 44,
                  bgcolor: 'grey.50',
                }}
              />
            }
          />
        </Skeleton>
      </CellFrame>
    )
  }
  return (
    <CellFrame
      sx={{
        position: 'relative',
        alignItems: responsive ? 'center' : undefined,
      }}
    >
      <Header
        elevation={0}
        position="absolute"
        color="transparent"
        maxWidth={responsive ? 1120 : undefined}
        sx={
          responsive
            ? {
                margin: '0 auto',
                bgcolor: 'transparent',
                pt: 0,
              }
            : {
                right: 'auto',
                maxWidth: 600,
                bgcolor: 'transparent',
                pt: 0,
              }
        }
        left={
          <IconButton.Close
            edge="start"
            bgcolor="grey.50"
            onClick={() =>
              router.goBack(
                generatePath(routerPages.ArtistStore, {
                  artistId:
                    (config.applicationType === 'standalone'
                      ? config.artistId
                      : router.query.artistId) ?? '#',
                })
              )
            }
          />
        }
      />
      <HeaderImageArea
        imageUri={imageUrl}
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        {...(responsive
          ? {
              maxWidth: 1120,
              sx: {
                margin: '0 auto',
                width: '100%',
                maxWidth: 1120,
              },
              imageProps: {
                sx: {
                  // 画面横幅が広がった時に画像が見切れないようにする
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                },
              },
            }
          : {})}
      />
    </CellFrame>
  )
}

type StoreCollectionListCellProps = {
  loading: State['loading']
  hasNextPage: State['hasNextPage']
  onFetchMore: () => Promise<void>
  collectionItems?: StoreCollectionItemsConnectionFragment
  artistId: string
  responsive: boolean
}
const StoreCollectionListCell: Cell<StoreCollectionListCellProps> = ({
  loading,
  hasNextPage,
  collectionItems,
  onFetchMore,
  artistId,
  responsive,
}) => {
  const { t } = useTranslation('storeCollectionDetail')
  const { analytics } = useAnalytics()
  const { router } = useRouter<{ collectionId: string }>()

  return (
    <CellFrame
      sx={{
        px: 5,
        pb: 5,
        alignItems: responsive ? 'center' : undefined,
      }}
    >
      <ScrollFetcher
        loading={loading}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMore}
        component={Grid2}
        componentProps={{ spacing: 3, container: true }}
        itemComponent={Grid2}
        itemComponentProps={{
          size: responsive ? { xs: 4, sm: 3 } : { xs: 4, md: 3 },
        }}
        nodes={collectionItems?.edges?.map(({ node }) => (
          <StoreItemArea
            key={node.item.id}
            showMenberOnlyIcon={true}
            trackView
            item={node.item as StoreItemFragment}
            artistId={artistId}
            {...(responsive
              ? {
                  responsive: true,
                  aspectRatio: '1 / 1',
                }
              : {})}
            onClick={() => {
              analytics?.track('select_item', {
                item_list_id: `collection_store_list_${router.query.collectionId}`,
                item_list_name: 'Collection Store Items',
                items: [
                  {
                    item_id: node.item.id,
                    item_name: node.item.title,
                    item_category: 'store_item',
                    item_category2: node.item.type,
                    item_category3: App.getPlatform(),
                    item_category4: 1,
                    item_category5: artistId,
                    price: node.item.price,
                  },
                ],
              })
            }}
          />
        ))}
        emptyMessage={t('productInfoEmptyMessage')}
        loadingComponent={
          <Grid2 container spacing={3} sx={{ py: 2 }}>
            {Array.from(Array(6).keys()).map(key => (
              <Grid2
                size={responsive ? { xs: 4, sm: 3 } : { xs: 4, md: 3 }}
                key={key}
              >
                <StoreItemAreaSkeleton responsive={responsive} />
              </Grid2>
            ))}
          </Grid2>
        }
      />
    </CellFrame>
  )
}

type CollectionDetailCellProps = {
  loading: State['loading']
  collection?: State['collection']
  onClickShare: Action['onClickShare']
  responsive: boolean
}
const CollectionDetailCell: Cell<CollectionDetailCellProps> = ({
  loading,
  collection,
  onClickShare,
  responsive,
}) => {
  const { t } = useTranslation('storeCollectionDetail')
  if (loading && !collection) {
    return (
      <CellFrame
        sx={{
          px: 5,
          py: 5,
          alignItems: responsive ? 'center' : undefined,
        }}
      >
        <Stack direction="row" sx={{ pb: 1 }}>
          <Stack flex={1} spacing={2}>
            <SkeletonText variant="large" width="20%" />
            <SkeletonText variant="large" width="50%" />
            <SkeletonText variant="small" width="30%" />
          </Stack>
          <Skeleton variant="rectangular" width={38} height={44} />
        </Stack>
        <Stack spacing={2}>
          <SkeletonText variant="body2" numberOfLine={3} lastWidth="60%" />
        </Stack>
      </CellFrame>
    )
  }
  return (
    <CellFrame
      sx={{
        px: 5,
        py: 5,
        alignItems: responsive ? 'center' : undefined,
      }}
    >
      <Stack direction="row">
        <Stack flex={1} spacing={1}>
          <Typography variant="subtitle2" color="text.secondary">
            {collection?.artist.displayName}
          </Typography>
          <Typography variant="h3">{collection?.title}</Typography>
          <Typography variant="caption">
            {t('itemNumber', { number: collection?.publishedItemCount })}
          </Typography>
        </Stack>
        <Stack>
          <Button
            variant="text"
            sx={{ px: 0, display: 'grid', gridTemplateColumns: '1fr' }}
          >
            <Icons.Share
              fontSize="small"
              sx={{ color: 'grey.600', mx: 'auto' }}
              onClick={onClickShare}
            />
            <Typography variant="overline" color="text.hint">
              {t('share')}
            </Typography>
          </Button>
        </Stack>
      </Stack>
      <Typography variant="body2">{collection?.description}</Typography>
    </CellFrame>
  )
}
